import { ReactElement, useEffect, useRef } from 'react'
import PageLayout from '../../../components/page-layout/page-layout'
import retriveMessages, { LocaleType } from '../../../messages/message'
import { NextPageWithLayout } from '../../../common/common-types'
import { GetStaticPropsContext } from 'next'
import { useChinaDetector } from '../../../common/region-locale'
import { usePuduMT1 } from '../../../hooks/use-pudumt1-data'
import { useRouter } from 'next/router'
import { useMobileDetector } from '../../../hooks/use-mobile-detector'
import { useTranslations } from 'next-intl'
import { getProductMeta } from '../../../common/seo'
import { EventReportManager } from '../../../event-report/event-report-manager'
import { withSEO } from '../../../common/with-seo'
import Banner from '../../../components/product-detail-components/banner'
import TitleMedia from '../../../components/product-detail-components/title-media'
import FullScreenMedia from '../../../components/product-detail-components/full-screen-media'
import InlineMediaDesc from '../../../components/product-detail-components/inline-media-desc'
import IOTSwiper from '../../../components/product-detail-components/iot-swiper'
import DesignAesthetics from '../../../components/product-detail-components/design-aesthetics'
import VerticalSingleMedia from '../../../components/standard/functionalModules/vertical-single-media'
import StandardProductParamsPanel from '../../../components/standard/commonModules/product-params-panel'
import FootnotePanel from '../../../components/standard/pd-footnote-panel'
import PdFloatRobot from '../../../components/pd-float-robot/pd-float-robot'
import useSWR from 'swr'
import { getProductBaseData, getProductDetailData, ProductDetailData } from '@/service/product'
import { MetaPage } from '@/common/meta-component-mgmt'
import { buildContactUsHref } from '../../../common/contact-us-link'

const PuduMT1: NextPageWithLayout<{ productDetailData: ProductDetailData }> = ({ productDetailData }) => {
  const inChina = useChinaDetector()
  const router = useRouter()
  const t = useTranslations()
  const isMobileDevice = useMobileDetector()
  const {
    section1Data,
    section2Data,
    section3Data,
    section4Data,
    section5Data,
    section6Data,
    section7Data,
    section8Data,
    section9Data,
    autoEmptyStationData,
    section10Data,
    section11Data,
    section12Data,
    section13Data,
    section14Data,
    autoEmptyStationParamsData,
    autoEmptyStationNoteData,
  } = usePuduMT1()
  const reported = useRef(false)
  const { data: productBaseData } = useSWR(getProductBaseData.swrkey(router.locale || '', 'mt1'), () =>
    getProductBaseData('mt1')
  )
  useEffect(() => {
    const meta = getProductMeta(router.route)
    if (meta && !reported.current) {
      reported.current = true
      EventReportManager.getInstance().fireEventImmediately(
        {
          data: {
            event: 'view_item',
            item_id: meta.id,
            item_name: meta.name,
            item_category: meta.category,
          },
        },
        true
      )
    }
  }, [])

  return (
    <>
      {productBaseData?.useDescription ? (
        <MetaPage productId="mt1" initialData={productDetailData} />
      ) : (
        <div id="pudumt1" className={['bg-white', 'w-full'].join(' ')}>
          <Banner {...section1Data} sloganTag="h1" />
          <FullScreenMedia {...section2Data} titleTag="h2" />
          <InlineMediaDesc data={section3Data.dataSource} itemTitleTag="h3" itemInfoTag="p" />
          <TitleMedia {...section4Data} titleTag="h2" />
          <FullScreenMedia {...section5Data} titleTag="h2" />
          <TitleMedia
            {...section6Data}
            titleTag="h2"
            extraNode={
              <div className={['flex flex-col gap-1', 'laptop:gap-2'].join(' ')}>
                <div className={["text-[#BCCF11] text-28 font-bold font-['Montserrat']", 'laptop:text-48'].join(' ')}>
                  {t('Section6.extraNode.data')}
                </div>
                <p className={['text-white/60 text-14', 'laptop:text-16'].join(' ')}>{t('Section6.extraNode.desc')}</p>
              </div>
            }
          />
          <FullScreenMedia {...section7Data} titleTag="h2" />
          <TitleMedia {...section8Data} titleTag="h2" />
          <InlineMediaDesc {...section9Data} titleTag="h2" itemTitleTag="h3" itemInfoTag="p" />
          <TitleMedia {...section10Data} titleTag="h2" descTags={['h3', 'p']} rowMediaDescTags={['h3', 'p']} />
          <VerticalSingleMedia {...autoEmptyStationData} titleTag="h2" />
          <InlineMediaDesc data={section11Data.dataSource} itemTitleTag="h2" itemInfoTag="p" />
          <IOTSwiper {...section12Data} titleTag="h2" itemTitleTag="h3" />
          <DesignAesthetics {...section13Data} titleTag="h2" />
          <StandardProductParamsPanel
            {...section14Data}
            title={t('parameterData.titleText')}
            backgroundClassName="bg-black"
            robot="mt1"
            titleTag="h2"
          />
          <StandardProductParamsPanel {...autoEmptyStationParamsData} titleTag="h2" />
          <FootnotePanel
            data={autoEmptyStationNoteData}
            containerClassName="px-6 py-15 laptop:px-0 laptop:py-15"
            textClassName="text-12 leading-5 font-normal text-white/95"
          />
          <PdFloatRobot
            robot={'https://cdn.pudutech.com/official-website/mt1/S1-3.webp'}
            color="#c0df16"
            href={buildContactUsHref('mt1')}
            hideCircleBorder={true}
            buttonText={t('RequestDemo')}
            buttonTextSize="small"
            buttonTextColor="rgba(0,0,0,.9)"
          />
        </div>
      )}
    </>
  )
}

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['product-mt1', 'products'], locale as LocaleType)
    const productDetailData = await getProductDetailData('mt1')
    return {
      props: {
        messages,
        productDetailData: productDetailData || [],
      },
      revalidate: 60,
    }
  },
  () => '/products/mt1'
)

PuduMT1.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export default PuduMT1
